import { useState } from 'react';
import Button from '../common/Button';
import { Label } from '../common/Form';
import MediaLibraryModal from '../MediaLibraryModal/MediaLibraryModal';
import MediaPreview from '../MediaPreview/MediaPreview';
import MultipleMediaUploader from '../MultipleMediaUploader';
import imageDisplay from '../../assets/images/ImageDisplay.png';
import { AlbumMediaInterface } from '../../types/AlbumMediaInterface';
import { BlobWrapper, MediaLibraryInterface, MenuItemMediaInterface } from '../../types/MediaInterface';
import { removeAtIndex } from '../../utils/general';

type MediaLibraryHeroProps = {
  variant: 'mediaLibrary';
  media: MenuItemMediaInterface[];
  onUpdate: (media: MenuItemMediaInterface[]) => void;
};

type UploaderHeroProps = {
  variant: 'uploader';
  media: (BlobWrapper | AlbumMediaInterface)[];
  onUpdate: (media: (BlobWrapper | AlbumMediaInterface)[]) => void;
};

type ProfileHeroMediaEditorProps = (MediaLibraryHeroProps | UploaderHeroProps) & {
  label: string;
  description: string;
  maxItems?: number;
  className?: string;
  labelClassName?: string;
  descriptionClassName?: string;
};

const ProfileHeroMediaEditor = (editorProps: ProfileHeroMediaEditorProps) => {
  const {
    variant,
    label,
    description,
    maxItems = 10,
    className = '',
    labelClassName = '',
    descriptionClassName = 'profile-hero-media-editor__description'
  } = editorProps;
  const [openMediaLibraryModal, setOpenMediaLibraryModal] = useState(false);

  if (variant === 'uploader') {
    const { media, onUpdate } = editorProps;
    return (
      <div className={className}>
        <Label label={label} className={labelClassName} />
        <p className={descriptionClassName}>{description}</p>
        <MultipleMediaUploader media={media} onUpdate={onUpdate} type="multi" maxImages={maxItems} maxTotalItems />
      </div>
    );
  }

  const { media, onUpdate } = editorProps;
  const canAddMore = media.length < maxItems;

  const handleMediaLibrarySelection = (selectedMedia: MediaLibraryInterface[]) => {
    const slotsRemaining = maxItems - media.length;
    if (slotsRemaining <= 0) {
      return;
    }

    onUpdate(
      media.concat(
        selectedMedia.slice(0, slotsRemaining).map(
          (mediaLibrary) =>
            ({
              mediaID: mediaLibrary.mediaID,
              mediaURL: mediaLibrary.mediaUrl,
              type: mediaLibrary.type
            } as MenuItemMediaInterface)
        )
      )
    );
    setOpenMediaLibraryModal(false);
  };

  const handleMediaLibraryRemoval = (mediaID: number) => {
    const idx = media.findIndex((item) => item?.mediaID === mediaID);
    if (idx >= 0) {
      onUpdate(removeAtIndex(media, idx));
    }
  };

  return (
    <div className={className}>
      <Label label={label} className={labelClassName} />
      <p className={descriptionClassName}>{description}</p>
      <div className="media-container">
        <Button className="select-media-button" onClick={() => setOpenMediaLibraryModal(true)} isDisabled={!canAddMore}>
          <img className="media-upload-icon" src={imageDisplay} alt="Icon indicating upload" />
          <p>Upload Media</p>
        </Button>
        <MediaPreview media={media} onUpdate={onUpdate} multiple hideEditVideo />
        {openMediaLibraryModal && (
          <MediaLibraryModal
            selectedMediaIDs={media.map((item) => item?.mediaID) ?? []}
            onSelect={handleMediaLibrarySelection}
            onRemove={handleMediaLibraryRemoval}
            onClose={() => setOpenMediaLibraryModal(false)}
          />
        )}
      </div>
    </div>
  );
};

export default ProfileHeroMediaEditor;
